import { makeK8sCreator, k8sRequest } from "../api";
import { formatAge } from "../format";
import { K8sObjectMeta } from "../types";

/**
 * A workload's revision history, and rolling back to one of them.
 *
 * Kubernetes already keeps this: every change to a DaemonSet's pod template
 * writes a ControllerRevision owned by it, holding the whole template. Nothing
 * is stored on our side - a second copy would only drift from the authority.
 */

export const STRATEGIC_MERGE_PATCH = "application/strategic-merge-patch+json";

export interface K8sControllerRevision {
    readonly metadata: K8sObjectMeta & {
        readonly ownerReferences?: { readonly uid?: string; readonly kind?: string; readonly name?: string }[];
    };
    readonly revision?: number;
    /** `{ spec: { template: { $patch: "replace", ... } } }` - the rollback body. */
    readonly data?: { readonly spec?: unknown };
}

export interface WorkloadRevision {
    readonly revision: number;
    readonly name: string;
    readonly created: string;
    readonly age: string;
    /** From `kubernetes.io/change-cause`; only set when something recorded it. */
    readonly changeCause: string;
    /**
     * The earlier revision this one restores, when its pod template is
     * identical to that revision's. Undefined when the revision is new content.
     */
    readonly restores?: number;
    /** The highest revision is what the workload is running now. */
    readonly current: boolean;
    /** The pod template, kept so two revisions can be compared. */
    readonly template: unknown;
    /** The patch body that rolls the workload back to this revision. */
    readonly patch: RollbackPatch;
}

const CHANGE_CAUSE = "kubernetes.io/change-cause";

/** Written by the portal when it rolls back, so the history says so outright. */
export const rollbackChangeCause = (revision: number): string =>
    `Rolled back to revision ${revision} in the Izuma Device Management Portal`;

const ROLLED_BACK_TO = /^Rolled back to revision (\d+)/;

/**
 * Which revision this one restores.
 *
 * The change cause is believed first, because a portal rollback records
 * exactly what it did. Otherwise the templates are compared: a rollback
 * reproduces an earlier revision's pod template byte for byte, so identical
 * content is the evidence that a revision is a restoration - and it catches
 * rollbacks made with kubectl, which record nothing unless --record was used.
 */
const restoredRevision = (
    changeCause: string,
    template: unknown,
    earlier: { revision: number; template: unknown }[]
): number | undefined => {
    const recorded = ROLLED_BACK_TO.exec(changeCause);
    if (recorded) {
        return parseInt(recorded[1], 10);
    }

    const serialised = JSON.stringify(template);
    const match = earlier.filter((other) => JSON.stringify(other.template) === serialised)[0];

    return match ? match.revision : undefined;
};

/**
 * Revisions belonging to one workload, newest first.
 *
 * Filtered by owner UID rather than by labels, which is what kubectl does and
 * is not safe here: two DaemonSets created from the same example manifest carry
 * the same pod labels, so a label query returns one workload's revisions under
 * another - and rolling back would then target the wrong workload.
 */
export const parseRevisions = (body: unknown, ownerUid: string): WorkloadRevision[] => {
    const list = (body || {}) as { items?: K8sControllerRevision[] };

    const mine = (list.items || []).filter((item) =>
        ((item.metadata || {}).ownerReferences || []).some((owner) => !!ownerUid && owner.uid === ownerUid)
    );

    const highest = mine.reduce((top, item) => Math.max(top, item.revision || 0), 0);

    // Oldest first while deriving, so each revision only looks backwards.
    const byAge = mine.slice().sort((a, b) => (a.revision || 0) - (b.revision || 0));
    const seen: { revision: number; template: unknown }[] = [];

    return byAge
        .map((item) => {
            const meta = item.metadata || ({} as K8sObjectMeta);
            const annotations = meta.annotations || {};
            const revision = item.revision || 0;
            const spec = (item.data || {}).spec;

            const changeCause = annotations[CHANGE_CAUSE] || "";
            const template = ((spec || {}) as { template?: unknown }).template;
            const restores = restoredRevision(changeCause, template, seen);

            seen.push({ revision, template });

            return {
                revision,
                name: meta.name || "",
                created: meta.creationTimestamp || "",
                age: formatAge(meta.creationTimestamp),
                changeCause,
                restores,
                current: revision === highest && highest > 0,
                template,
                patch: { spec },
            };
        })
        .sort((a, b) => b.revision - a.revision);
};

export interface FieldChange {
    /** Dotted path into the pod template, e.g. "spec.containers[0].image". */
    readonly path: string;
    readonly from: string;
    readonly to: string;
}

const NOT_SET = "(not set)";

const render = (value: unknown): string => (value === undefined ? NOT_SET : `${JSON.stringify(value)}`);

/** Flatten to leaf paths, so two templates can be compared field by field. */
const leaves = (value: unknown, path: string, into: { [path: string]: unknown }): void => {
    if (Array.isArray(value)) {
        value.forEach((item, index) => leaves(item, `${path}[${index}]`, into));

        return;
    }

    if (value && typeof value === "object") {
        const record = value as { [key: string]: unknown };
        Object.keys(record).forEach((key) => leaves(record[key], path ? `${path}.${key}` : key, into));

        return;
    }

    into[path] = value;
};

/**
 * Every leaf field that differs between two revisions.
 *
 * A summary of image and ports is not enough: a real rollback on our own
 * cluster changed nothing but `spec.tolerations[0].effect`, which every
 * summary view - including `kubectl rollout undo --dry-run` - renders as no
 * change at all. That is exactly when an operator needs to see the difference.
 */
export const diffTemplates = (from: unknown, to: unknown): FieldChange[] => {
    const a: { [path: string]: unknown } = {};
    const b: { [path: string]: unknown } = {};

    leaves(from, "", a);
    leaves(to, "", b);

    const paths = Object.keys(a)
        .concat(Object.keys(b).filter((path) => !(path in a)))
        .sort();

    return paths
        .filter((path) => JSON.stringify(a[path]) !== JSON.stringify(b[path]))
        .map((path) => ({ path, from: render(a[path]), to: render(b[path]) }));
};

/** The body of a rollback: the chosen revision's own `data.spec`. */
export interface RollbackPatch {
    readonly metadata?: { readonly annotations: { [key: string]: string } };
    readonly spec?: unknown;
}

/**
 * The revision's stored spec, plus a note of what this was. Kubernetes copies
 * the DaemonSet's annotations onto the revision it writes, which is how the
 * change cause reaches the history at all.
 */
export const rollbackPatch = (revision: WorkloadRevision): RollbackPatch => ({
    metadata: { annotations: { [CHANGE_CAUSE]: rollbackChangeCause(revision.revision) } },
    spec: (revision.patch as { spec?: unknown }).spec,
});

/**
 * The owner UID is only known once the workload has loaded, and a creator's
 * parse function is fixed when it is built, so this returns the raw list and
 * the container calls parseRevisions with the UID.
 */
const rawRevisions = makeK8sCreator<unknown>("K8S_WORKLOAD_REVISIONS", (body) => body);

const rolledBack = makeK8sCreator<unknown, RollbackPatch>("K8S_WORKLOAD_ROLLBACK", (body) => body);

const getRevisionsAction = (labelSelector: string, namespace = "default") =>
    rawRevisions.createThunk(
        k8sRequest(`apis/apps/v1/namespaces/${encodeURIComponent(namespace)}/controllerrevisions`, { labelSelector })
    );

/** The request options for a rollback, separated out so they can be asserted. */
export const rollbackRequest = (name: string, patch: RollbackPatch, namespace = "default") => ({
    ...k8sRequest(`apis/apps/v1/namespaces/${encodeURIComponent(namespace)}/daemonsets/${encodeURIComponent(name)}`),
    method: "PATCH" as const,
    // `type`, not a Content-Type header: ajax.ts calls req.type() after
    // .set(headers), so a header is overwritten by the default and the API
    // server answers 415. Same trap as the node label patch.
    type: STRATEGIC_MERGE_PATCH,
    data: patch,
});

/**
 * Apply a revision's stored template to the workload.
 *
 * This is what `kubectl rollout undo` sends: the revision's own `data.spec`,
 * whose template carries `"$patch": "replace"`, so the template is replaced
 * rather than merged field by field.
 */
const rollbackAction = (name: string, patch: RollbackPatch, namespace = "default") =>
    rolledBack.createThunk(rollbackRequest(name, patch, namespace));

const resetRollbackAction = () => rolledBack.createResetAction();

export { getRevisionsAction, rollbackAction, resetRollbackAction };
export const revisionsReducer = rawRevisions.reducer;
export const rollbackReducer = rolledBack.reducer;
